import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Banknote, Warehouse, Boxes, HandCoins, Scale, TrendingUp, ArrowRight } from "lucide-react";

export default function BalanceSheet() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/balance-sheet").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-slate-500">Memuat...</div>;

  const AssetRow = ({ icon: Icon, label, value, sub, color = "text-slate-700" }) => (
    <div className="flex items-center justify-between p-4 border-b border-[var(--hotto-border)] last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-emerald-50`}>
          <Icon className={`w-4 h-4 text-[var(--hotto-primary)]`}/>
        </div>
        <div>
          <div className="text-sm font-medium">{label}</div>
          {sub && <div className="text-xs text-slate-500">{sub}</div>}
        </div>
      </div>
      <div className={`font-mono-num font-semibold ${color}`}>{formatIDR(value)}</div>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Neraca Sederhana"
        subtitle="Aset vs Kewajiban vs Ekuitas · net worth bisnis Anda"
        testId="page-balance-sheet"
      />

      {/* Net Worth Hero */}
      <Card className={`p-6 border-2 rounded-md mb-6 stat-card ${d.net_worth>=0?"border-[var(--hotto-primary)] bg-emerald-50/30":"border-red-400 bg-red-50/30"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Scale className={`w-5 h-5 ${d.net_worth>=0?"text-[var(--hotto-primary)]":"text-red-600"}`}/>
              <div className="overline">Kekayaan Bersih Bisnis (Net Worth)</div>
            </div>
            <div className={`font-mono-num text-4xl font-bold ${d.net_worth>=0?"text-[var(--hotto-primary)]":"text-red-600"}`} data-testid="net-worth">
              {formatIDR(d.net_worth)}
            </div>
          </div>
          <div className="text-right text-sm text-slate-600">
            <div className="text-xs text-slate-500 mb-1">= Total Aset - Kewajiban</div>
            <div className="font-mono-num">{formatIDR(d.assets.total)} - {formatIDR(d.liabilities.total)}</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* ASET */}
        <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
          <div className="p-4 bg-emerald-50 border-b border-[var(--hotto-border)]">
            <div className="overline text-[var(--hotto-primary)] mb-1">ASET</div>
            <div className="flex justify-between items-baseline">
              <div className="text-lg font-bold" style={{fontFamily:'Outfit'}}>Kekayaan yang Dimiliki</div>
              <div className="font-mono-num text-xl font-bold text-[var(--hotto-primary)]">{formatIDR(d.assets.total)}</div>
            </div>
          </div>
          <div>
            <AssetRow icon={Banknote} label="Kas" value={d.assets.cash} sub="Uang tunai + bank"
              color={d.assets.cash>=0?"text-[var(--hotto-success)]":"text-red-600"}/>
            <AssetRow icon={Warehouse} label="Stok Gudang" value={d.assets.warehouse_stock}
              sub={`${d.assets.warehouse_qty} unit × harga beli`}/>
            <AssetRow icon={Boxes} label="Stok Titipan di Toko" value={d.assets.consignment_cost}
              sub={`${d.assets.consignment_qty} unit · retail: ${formatIDR(d.assets.consignment_retail)}`}/>
            <AssetRow icon={HandCoins} label="Piutang Toko" value={d.assets.piutang_toko}
              sub="Estimasi barang terjual toko yang belum dibayar"/>
          </div>
        </Card>

        {/* KEWAJIBAN + EKUITAS */}
        <div className="space-y-6">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-4 bg-red-50 border-b border-[var(--hotto-border)]">
              <div className="overline text-red-700 mb-1">KEWAJIBAN</div>
              <div className="flex justify-between items-baseline">
                <div className="text-lg font-bold" style={{fontFamily:'Outfit'}}>Utang / Kewajiban</div>
                <div className="font-mono-num text-xl font-bold text-red-600">{formatIDR(d.liabilities.total)}</div>
              </div>
            </div>
            {(d.liabilities.debts || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Belum ada hutang belum lunas.</div>
            ) : (
              <div className="divide-y divide-[var(--hotto-border)]">
                {d.liabilities.debts.map((debt) => (
                  <div key={debt.id} className="p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{debt.creditor}</div>
                      <div className="text-xs text-slate-500">
                        {debt.due_date ? `Jatuh tempo ${new Date(debt.due_date).toLocaleDateString("id-ID",{day:"2-digit",month:"short",year:"numeric"})}` : "Tanpa tempo"}
                        {debt.notes && ` · ${debt.notes.slice(0,30)}`}
                      </div>
                    </div>
                    <div className="font-mono-num font-semibold text-red-600">{formatIDR(debt.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-4 bg-slate-100 border-b border-[var(--hotto-border)]">
              <div className="overline mb-1">EKUITAS</div>
              <div className="flex justify-between items-baseline">
                <div className="text-lg font-bold" style={{fontFamily:'Outfit'}}>Modal Owner</div>
                <div className="font-mono-num text-xl font-bold">{formatIDR(d.equity.total)}</div>
              </div>
            </div>
            <div className="divide-y divide-[var(--hotto-border)]">
              <div className="p-3 flex justify-between text-sm">
                <span className="text-slate-600">Modal Awal</span>
                <span className="font-mono-num">{formatIDR(d.equity.modal_awal)}</span>
              </div>
              <div className="p-3 flex justify-between text-sm">
                <span className="text-slate-600">Penyesuaian Modal (Setor/Tarik)</span>
                <span className={`font-mono-num ${d.equity.modal_adjustments>=0?"text-emerald-600":"text-red-600"}`}>{d.equity.modal_adjustments>=0?"+":""}{formatIDR(d.equity.modal_adjustments)}</span>
              </div>
              <div className="p-3 flex justify-between text-sm">
                <span className="text-slate-600">Laba Ditahan (Retained Earnings)</span>
                <span className={`font-mono-num ${d.equity.retained_earnings>=0?"text-emerald-600":"text-red-600"}`}>{d.equity.retained_earnings>=0?"+":""}{formatIDR(d.equity.retained_earnings)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Piutang Detail */}
      {d.piutang_detail.length > 0 && (
        <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-[var(--hotto-border)] overline">Detail Piutang & Aktivitas Toko</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Toko</TableHead>
                <TableHead className="text-right">Total Disalurkan</TableHead>
                <TableHead className="text-right">Total Dibayar</TableHead>
                <TableHead className="text-right">Nilai Titipan Sisa</TableHead>
                <TableHead className="text-right">Piutang (Est.)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.piutang_detail.map((p) => (
                <TableRow key={p.store_id} className="table-row-hover">
                  <TableCell className="font-medium">{p.store_name}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(p.distributed)}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-600">{formatIDR(p.paid)}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(p.remaining_retail)}</TableCell>
                  <TableCell className="text-right font-mono-num">
                    {p.piutang > 0 ? (
                      <Badge className="bg-orange-50 text-orange-700 hover:bg-orange-50">{formatIDR(p.piutang)}</Badge>
                    ) : <span className="text-slate-400">Rp 0</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <div className="text-xs text-slate-500 mt-4">
        <b>Catatan:</b> Piutang toko = estimasi barang yang sudah tidak ada di titipan tapi belum dilaporkan penjualannya (Disalurkan − Dibayar − Sisa Titipan). Retained Earnings = Net Worth − Modal Awal − Penyesuaian.
      </div>
    </>
  );
}
